import { useCallback, useEffect, useRef, useState } from "react";

import { countUnreadNotifications } from "~/graphql/queries";
import { onNotificationCreates } from "~/graphql/subscriptions";
import appsyncClient from "~/lib/appsyncClient";
import { withRetry } from "~/lib/graphqlRetry";
import { createManagedSubscription } from "~/lib/subscriptionManager";

// Maximum number of pending notifications to track during initialization
// This prevents unbounded memory growth if initialization is slow/stuck
const MAX_PENDING_NOTIFICATIONS = 100;

// Cache key for localStorage
const getCacheKey = (userId, teamId) =>
  `brokerkit_unread_count_${userId}_${teamId}`;

// Read cached count from localStorage
const getCachedCount = (userId, teamId) => {
  try {
    const cached = localStorage.getItem(getCacheKey(userId, teamId));
    if (!cached) {
      return 0;
    }
    const parsed = parseInt(cached, 10);
    return Number.isNaN(parsed) ? 0 : parsed;
  } catch {
    return 0;
  }
};

// Save count to localStorage
const setCachedCount = (userId, teamId, count) => {
  try {
    localStorage.setItem(getCacheKey(userId, teamId), String(count));
  } catch {
    // Ignore localStorage errors (quota exceeded, private browsing, etc.)
  }
};

const useUnreadNotificationsCount = () => {
  const { currentUser, currentTeam } = Rails.helpers;
  const UserID = currentUser?.id;
  const TeamID = currentTeam?.id;

  // Initialize with cached value for instant display
  const [unreadNotifications, setUnreadNotifications] = useState(() =>
    getCachedCount(UserID, TeamID),
  );

  // Track initialization to prevent race condition between fetch and subscription
  const isInitializedRef = useRef(false);
  // Track IDs of notifications received via subscription during initial fetch
  const pendingNotificationIdsRef = useRef(new Set());

  useEffect(() => {
    let isMounted = true;
    let graphqlPromise = null;
    isInitializedRef.current = false;

    const fetchUnreadNotificationsCount = async () => {
      try {
        graphqlPromise = withRetry(() =>
          appsyncClient.graphql({
            query: countUnreadNotifications,
            variables: { UserID, TeamID },
          }),
        );
        const result = await graphqlPromise;

        if (
          isMounted &&
          result.data &&
          result.data.countUnreadNotifications !== null
        ) {
          const serverCount = result.data.countUnreadNotifications;
          // Add any notifications that arrived during fetch but might not be in the count
          const pendingCount = pendingNotificationIdsRef.current.size;
          setUnreadNotifications(serverCount + pendingCount);
          pendingNotificationIdsRef.current.clear();
          isInitializedRef.current = true;
        }
      } catch (error) {
        // Ignore cancellation errors
        if (!appsyncClient.isCancelError?.(error)) {
          console.error("Failed to fetch unread notifications count:", error);
        }
        // Still mark as initialized so subscription can work
        if (isMounted) {
          isInitializedRef.current = true;
          pendingNotificationIdsRef.current.clear();
        }
      }
    };

    fetchUnreadNotificationsCount();

    return () => {
      isMounted = false;
      // Clear pending notifications to prevent memory leak on cleanup
      pendingNotificationIdsRef.current.clear();
      if (graphqlPromise) {
        appsyncClient.cancel?.(graphqlPromise);
      }
    };
  }, [UserID, TeamID]);

  // Subscribe only to NEW notifications (not read status updates)
  // Read status updates should be handled by useNotificationStatus
  useEffect(() => {
    const handleData = (data) => {
      const notification = data.onNotificationCreates;
      if (!notification) {
        return;
      }

      // Only count unread notifications
      if (notification.IsRead) {
        return;
      }

      if (!isInitializedRef.current) {
        // Initial fetch still in progress - track this notification
        // It might not be included in the fetch result if it was created after the query started
        // Limit set size to prevent memory leak if initialization is slow/stuck
        if (
          pendingNotificationIdsRef.current.size < MAX_PENDING_NOTIFICATIONS
        ) {
          pendingNotificationIdsRef.current.add(notification.ID);
        }
      } else {
        // Already initialized - safe to increment
        setUnreadNotifications((prev) => prev + 1);
      }
    };

    const handleError = (error) => {
      console.error("Notification subscription error:", error);
    };

    const controller = createManagedSubscription({
      query: onNotificationCreates,
      variables: { UserID, TeamID },
      onData: handleData,
      onError: handleError,
    });

    return () => controller.unsubscribe();
  }, [UserID, TeamID]);

  // Update localStorage cache whenever count changes
  useEffect(() => {
    if (isInitializedRef.current) {
      setCachedCount(UserID, TeamID, unreadNotifications);
    }
  }, [unreadNotifications, UserID, TeamID]);

  // Provide a function to adjust count (for use by useNotificationStatus)
  const adjustUnreadCount = useCallback((delta) => {
    setUnreadNotifications((prev) => Math.max(0, prev + delta));
  }, []);

  return [unreadNotifications, setUnreadNotifications, adjustUnreadCount];
};

export default useUnreadNotificationsCount;
